import { INSTAGRAM_WIDGET_NAMES } from "@/constants/widget-names";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";
import { map, switchMap, tap } from "rxjs";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import InstagramGalleryInstagramPageWidget from "./InstagramGalleryInstagramPageWidget";
import InstagramGalleryMainPageWidget from "./InstagramGalleryMainPageWidget";
import InstagramGalleryProductPageWidget from "./InstagramGalleryProductPageWidget";

// TODO: delete this block in second iteration after theme-extension deployment
const OLD_GALLERY_LAYOUT_DATA_ATTR = "widget";
const OLD_SHOW_STORIES_DATA_ATTR = "show-stories";
const OLD_TAGGED_ONLY_DATA_ATTR = "tagged";
const OLD_GALLERY_TITLE_DATA_ATTR = "gallery-title";
const OLD_HIGH_QUALITY_DATA_ATTR = "high-quality";
const OLD_GALLERY_ID_DATA_ATTR = "gallery_id";
const OLD_SHOP_INSTAGRAM_PAGE = "shop-instagram-page";
const OLD_PRODUCT_ID_DATA_ATTR = "product_id";

const GALLERY_LAYOUT_DATA_ATTR = "gw-gallery-layout";
const SHOW_STORIES_DATA_ATTR = "gw-show-stories";
const TAGGED_ONLY_DATA_ATTR = "gw-tagged";
const GALLERY_TITLE_DATA_ATTR = "gw-gallery-title";
const HIGH_QUALITY_DATA_ATTR = "gw-high-quality";
const GALLERY_ID_DATA_ATTR = "gw-gallery-id";
const SHOP_INSTAGRAM_PAGE = "gw-shop-instagram-page";
const PRODUCT_ID_DATA_ATTR = "gw-product-id";

class InstagramGalleryWidgetInjector {
    private ioi: IntersectionObserverInjector;

    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly moi: MutationObserverInjector
    ) {
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            this.placeholderSelector,
            {
                rootMargin: "0px 0px 50% 0px",
            }
        );
    }

    start(): void {
        this.logger?.debug("InstagramGalleryWidgetInjector.start", {
            placeholderSelector: this.placeholderSelector,
        });

        this.ioi.targets$
            .pipe(
                switchMap((placeholder) => {
                    this.logger?.debug(
                        "InstagramGalleryWidgetInjector found placeholder",
                        {
                            placeholder,
                        }
                    );

                    let galleryId = parseIntDataAttr(
                        placeholder as HTMLElement,
                        GALLERY_ID_DATA_ATTR
                    );
                    if (galleryId === null) {
                        galleryId = parseIntDataAttr(
                            placeholder as HTMLElement,
                            OLD_GALLERY_ID_DATA_ATTR
                        );

                        if (galleryId !== null) {
                            this.eventsCollector.pushEvent(
                                this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                                    INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET,
                                    OLD_GALLERY_ID_DATA_ATTR
                                )
                            );
                        }
                    }

                    let galleryLayout = parseIntDataAttr(
                        placeholder as HTMLElement,
                        GALLERY_LAYOUT_DATA_ATTR
                    );
                    if (galleryLayout === null) {
                        galleryLayout = parseIntDataAttr(
                            placeholder as HTMLElement,
                            OLD_GALLERY_LAYOUT_DATA_ATTR
                        );

                        if (galleryLayout !== null) {
                            this.eventsCollector.pushEvent(
                                this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                                    INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET,
                                    OLD_GALLERY_LAYOUT_DATA_ATTR
                                )
                            );
                        }
                    }

                    let productIdFromDataAttr =
                        parseIntDataAttr(
                            placeholder as HTMLElement,
                            PRODUCT_ID_DATA_ATTR
                        ) ||
                        parseIntFromGidDataAttr(
                            placeholder as HTMLElement,
                            PRODUCT_ID_DATA_ATTR
                        );
                    if (productIdFromDataAttr === null) {
                        productIdFromDataAttr = parseIntDataAttr(
                            placeholder as HTMLElement,
                            OLD_PRODUCT_ID_DATA_ATTR
                        );

                        if (productIdFromDataAttr !== null) {
                            this.eventsCollector.pushEvent(
                                this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                                    INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET,
                                    OLD_PRODUCT_ID_DATA_ATTR
                                )
                            );
                        }
                    }

                    let storiesEnabled = parseStringDataAttr(
                        placeholder as HTMLElement,
                        SHOW_STORIES_DATA_ATTR
                    );
                    if (storiesEnabled === null) {
                        storiesEnabled = parseStringDataAttr(
                            placeholder as HTMLElement,
                            OLD_SHOW_STORIES_DATA_ATTR
                        );

                        if (storiesEnabled !== null) {
                            this.eventsCollector.pushEvent(
                                this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                                    INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET,
                                    OLD_SHOW_STORIES_DATA_ATTR
                                )
                            );
                        }
                    }

                    let taggedOnly = parseStringDataAttr(
                        placeholder as HTMLElement,
                        TAGGED_ONLY_DATA_ATTR
                    );
                    if (taggedOnly === null) {
                        taggedOnly = parseStringDataAttr(
                            placeholder as HTMLElement,
                            OLD_TAGGED_ONLY_DATA_ATTR
                        );

                        if (taggedOnly !== null) {
                            this.eventsCollector.pushEvent(
                                this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                                    INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET,
                                    OLD_TAGGED_ONLY_DATA_ATTR
                                )
                            );
                        }
                    }

                    let galleryTitle = parseStringDataAttr(
                        placeholder as HTMLElement,
                        GALLERY_TITLE_DATA_ATTR
                    );
                    if (galleryTitle === null) {
                        galleryTitle = parseStringDataAttr(
                            placeholder as HTMLElement,
                            OLD_GALLERY_TITLE_DATA_ATTR
                        );

                        if (galleryTitle !== null) {
                            this.eventsCollector.pushEvent(
                                this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                                    INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET,
                                    OLD_GALLERY_TITLE_DATA_ATTR
                                )
                            );
                        }
                    }

                    let highQualityEnabled = parseStringDataAttr(
                        placeholder as HTMLElement,
                        HIGH_QUALITY_DATA_ATTR
                    );
                    if (highQualityEnabled === null) {
                        highQualityEnabled = parseStringDataAttr(
                            placeholder as HTMLElement,
                            OLD_HIGH_QUALITY_DATA_ATTR
                        );

                        if (highQualityEnabled !== null) {
                            this.eventsCollector.pushEvent(
                                this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                                    INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET,
                                    OLD_HIGH_QUALITY_DATA_ATTR
                                )
                            );
                        }
                    }

                    let shopInstagramPage = parseStringDataAttr(
                        placeholder as HTMLElement,
                        SHOP_INSTAGRAM_PAGE
                    );
                    if (shopInstagramPage === null) {
                        shopInstagramPage = parseStringDataAttr(
                            placeholder as HTMLElement,
                            OLD_SHOP_INSTAGRAM_PAGE
                        );

                        if (shopInstagramPage !== null) {
                            this.eventsCollector.pushEvent(
                                this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                                    INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET,
                                    OLD_SHOP_INSTAGRAM_PAGE
                                )
                            );
                        }
                    }

                    if (!shopInstagramPage) {
                        if (galleryId === null) {
                            throw new Error(
                                `Placeholder does not have data-${GALLERY_ID_DATA_ATTR} attribute.`
                            );
                        }

                        if (galleryLayout === null) {
                            throw new Error(
                                `Placeholder does not have data-${GALLERY_LAYOUT_DATA_ATTR} attribute.`
                            );
                        }
                    }

                    const productIdManager = new ProductIdManager();

                    if (productIdFromDataAttr !== null) {
                        productIdManager.registerProvider(
                            new ProductIdFromStaticProvider(
                                productIdFromDataAttr
                            )
                        );
                    }

                    return productIdManager.productId$.pipe(
                        map((productId) => ({
                            placeholder,
                            galleryId,
                            galleryLayout,
                            productId,
                            storiesEnabled,
                            taggedOnly,
                            galleryTitle,
                            highQualityEnabled,
                            shopInstagramPage,
                        }))
                    );
                }),
                tap(
                    ({
                        placeholder,
                        galleryId,
                        galleryLayout,
                        productId,
                        storiesEnabled,
                        taggedOnly,
                        galleryTitle,
                        highQualityEnabled,
                        shopInstagramPage,
                    }) => {
                        const WidgetCls =
                            galleryId !== null && productId === null
                                ? InstagramGalleryMainPageWidget
                                : galleryId !== null && productId !== null
                                ? InstagramGalleryProductPageWidget
                                : shopInstagramPage
                                ? InstagramGalleryInstagramPageWidget
                                : undefined;

                        if (!WidgetCls) {
                            throw new Error(
                                "Incorrect data-attributes set in placeholder. We can't detect which widget needs to be used"
                            );
                        }

                        const widget = new WidgetCls(
                            {
                                galleryId: galleryId,
                                galleryLayout: galleryLayout || 0,
                                isInstaPage: !!shopInstagramPage,
                                productId: productId,
                                customSettings: {
                                    storiesEnabled: !!storiesEnabled,
                                    filter: taggedOnly ? "only_tagged" : "all",
                                    galleryTitle: galleryTitle,
                                    highQualityEnabled: !!highQualityEnabled,
                                    forceDisabledStories: false,
                                },
                                target: placeholder as HTMLElement,
                            },
                            this.logger
                        );

                        widget.init();

                        this.eventsCollector.pushEvent(
                            this.analyticsEventsFactory.createWidgetInjected(
                                INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET
                            )
                        );
                        dispatchSdkEvent(
                            new WidgetInjectedSdkEvent(
                                INSTAGRAM_WIDGET_NAMES.INSTAGRAM_GALLERY_WIDGET
                            )
                        );
                    }
                )
            )
            .subscribe();

        this.ioi.start();
    }
}

export default InstagramGalleryWidgetInjector;
